/**
 * Semrush CSV Parser (Story 20.4, AC #5)
 *
 * Semrush's Analytics + Trends APIs both return semicolon-delimited CSV
 * (NOT JSON) per developer.semrush.com/api/v3/analytics/ verified 2026-05-08.
 *
 * Three non-standard quirks vs. the Node `csv-parse` defaults:
 *
 *  1. **Semicolon delimiters** — `csv-parse` defaults to commas; Semrush
 *     wedges `,`-separated trend data into the value space, so the row-level
 *     delimiter must be `;`.
 *
 *  2. **First-row headers** — every successful response begins with the
 *     header row, then 0..N data rows. The header row MUST NOT count toward
 *     `recordCount`.
 *
 *  3. **Plaintext error responses** — when Semrush errors (rate-limit,
 *     insufficient units, bad request) it returns plaintext like
 *     `ERROR 50 :: NOTHING FOUND` with HTTP 200 and Content-Type text/plain.
 *     The parser detects this BEFORE attempting CSV parse and throws
 *     `SemrushApiCallError` so the caller maps to AdapterErrorCode per AC #7.
 *
 * The parser also handles:
 *  - URL-encoded values (Semrush sends with `export_decode=1` by default; we
 *    `decodeURIComponent` defensively as belt-and-braces).
 *  - Escaped quoted fields when `export_escape=1`: `"\"value with ; semi\""`
 *    decodes to `value with ; semi`.
 *  - Empty trailing lines (some Semrush responses end with `\n`).
 *  - Optional `expectedHeaders` enforcement (defends against silent
 *    column-reordering by Semrush across API versions).
 */

import {
  parseSemrushApiError,
  SemrushApiCallError,
  SEMRUSH_PLAINTEXT_ERROR_REGEX,
} from "./errors";

// ============================================================================
// Types
// ============================================================================

export interface SemrushCsvResult {
  /** Parsed headers — order matches the first row of the response body. */
  readonly headers: ReadonlyArray<string>;
  /** Parsed rows. Each row is a Record<header, string>. */
  readonly rows: ReadonlyArray<Record<string, string>>;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Split a single CSV-line on `;` while respecting double-quoted fields
 * (`export_escape=1`). Inside a quoted field, `""` represents a literal `"`.
 *
 * Bounded — no `.*` quantifier — so adversarial 10MB+ payloads don't trigger
 * pathological backtracking. Simple state machine keeps the worst-case
 * complexity O(n).
 */
function splitSemrushCsvLine(line: string): string[] {
  const fields: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i]!;
    if (inQuotes) {
      if (c === '"') {
        // Lookahead: `""` is an escaped literal quote inside a quoted field.
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += c;
      }
    } else {
      if (c === '"' && current.length === 0) {
        inQuotes = true;
      } else if (c === ";") {
        fields.push(current);
        current = "";
      } else {
        current += c;
      }
    }
  }
  fields.push(current);
  return fields;
}

/**
 * Defensive `decodeURIComponent` that returns the original value if the
 * input contains a malformed `%xx` sequence. Semrush occasionally serves
 * raw-not-encoded strings on legacy reports — bare `decodeURIComponent`
 * would throw `URIError` on those.
 */
function safeDecodeURIComponent(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

// ============================================================================
// Public parser (AC #5)
// ============================================================================

/**
 * Parse a Semrush CSV response body into headers + rows.
 *
 * Two-phase processing:
 *
 *  1. **Plaintext-error detection** — if the body matches
 *     `/^ERROR \d+ ::/` we throw `SemrushApiCallError`. The caller catches
 *     and maps via `parseSemrushApiError` per AC #7.
 *
 *  2. **CSV parse** — split on `\n`, take first non-empty line as headers,
 *     iterate remaining lines into Record<header, string>.
 *
 * @param body - The raw response body (text/plain or text/csv).
 * @param expectedHeaders - When provided, asserts the response's first-row
 *   headers match exactly (in order). Defends against silent column
 *   reordering by Semrush across API versions.
 * @returns SemrushCsvResult with parsed headers + rows.
 * @throws SemrushApiCallError on plaintext-error body OR header-mismatch
 *   when `expectedHeaders` is provided.
 */
export function parseSemrushCsv(
  body: string,
  expectedHeaders?: ReadonlyArray<string>
): SemrushCsvResult {
  if (typeof body !== "string") {
    throw new TypeError("parseSemrushCsv: body must be a string");
  }

  // **Code Review Pass 1 (Edge Case Hunter) — strip UTF-8 BOM.** A leading
  // `﻿` would otherwise prevent the plaintext-error regex from
  // matching `^ERROR \d+`, falling through to the CSV path and surfacing
  // a generic `validation_error` instead of the real numbered code.
  const trimmedBom = body.charCodeAt(0) === 0xfeff ? body.slice(1) : body;

  // Phase 1 — plaintext error detection. Even on HTTP 200 responses,
  // Semrush may serve `ERROR 132 :: API UNITS BALANCE IS ZERO` etc.
  if (SEMRUSH_PLAINTEXT_ERROR_REGEX.test(trimmedBom)) {
    const parsed = parseSemrushApiError(trimmedBom, 200);
    if (parsed) {
      throw new SemrushApiCallError(parsed);
    }
  }

  // Phase 2 — CSV parse.
  // Split on LF, CRLF, AND lone-CR (legacy / proxy-rewritten responses).
  // Without lone-CR support, an old-Mac-style `\r`-only response collapses
  // into a single line, embeds the CR in the header string, and silently
  // drops every data row.
  const lines = trimmedBom.split(/\r\n|\r|\n/);
  // Trim empty trailing lines (Semrush often emits `header\nrow\n`).
  while (lines.length > 0 && lines[lines.length - 1]!.length === 0) {
    lines.pop();
  }
  if (lines.length === 0) {
    // Empty body = empty result-set (e.g. /summary called on an account with
    // no entitled targets but Trends entitlement still active). Honor the
    // expectedHeaders contract — caller knows what it asked for.
    return {
      headers: expectedHeaders ?? [],
      rows: [],
    };
  }

  const headerRow = lines[0]!;
  // If the first row has no semicolons AND no recognizable column header,
  // Semrush served us a plaintext blob the parser can't decode (missing the
  // ERROR prefix but still not CSV). Treat as malformed-response to avoid
  // misparsing as a single-row record.
  if (!headerRow.includes(";")) {
    // Single-column responses ARE possible (e.g. plaintext balance from
    // /users/countapiunits.html). The countapiunits endpoint should NOT use
    // this parser; if it ends up here it's a bug. Fail loud to surface it.
    throw new SemrushApiCallError({
      type: "validation_error",
      code: null,
      message: `Semrush response is not semicolon-delimited CSV (first 80 chars: "${headerRow.slice(0, 80)}")`,
      status: 200,
      retryable: false,
    });
  }

  const headers = splitSemrushCsvLine(headerRow).map(safeDecodeURIComponent);

  if (expectedHeaders) {
    if (
      expectedHeaders.length !== headers.length ||
      !expectedHeaders.every((h, i) => h === headers[i])
    ) {
      throw new SemrushApiCallError({
        type: "validation_error",
        code: null,
        message: `Semrush response headers do not match expected: got [${headers.join(", ")}], expected [${expectedHeaders.join(", ")}]`,
        status: 200,
        retryable: false,
      });
    }
  }

  const rows: Record<string, string>[] = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i]!;
    if (line.length === 0) continue; // skip mid-body blank lines
    const fields = splitSemrushCsvLine(line);
    const row: Record<string, string> = {};
    for (let j = 0; j < headers.length; j++) {
      const value = fields[j] ?? "";
      row[headers[j]!] = safeDecodeURIComponent(value);
    }
    rows.push(row);
  }

  return { headers, rows };
}
